import { Check, Copy, Locate, PanelRightOpen } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from 'shared-ui';

import type { NetworkTableAreaRow, NetworkTableDmaRow, NetworkTablePoiRow } from '@/api/networkTableApi';
import { buildPoiClipboardText } from '@/components/map/utils/poiClipboard';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { showErrorToast } from '@/errors/showErrorToast';

import { mapTablePoiToFeatureProperties, tablePoiCoords } from './mapTablePoiToFeatureProperties';

/** Matches the POI details panel's copy feedback. */
const COPIED_FEEDBACK_MS = 1500;

interface PoiRowActionsProps {
    poi: NetworkTablePoiRow;
    area: NetworkTableAreaRow;
    dma: NetworkTableDmaRow;
    /** Copy and Show Details are maximised-only; Locate shows in both open layouts. */
    isMaximised: boolean;
    onLocate: (poi: NetworkTablePoiRow) => void;
    onShowDetails: (poi: PoiFeatureProperties, coords: [number, number]) => void;
}

/**
 * The per-POI actions. One implementation for both layouts — which actions appear is a
 * presentation rule, not a second row component.
 *
 * Copy and Show Details are driven by the SAME converted properties object, so the clipboard text
 * and the details panel can never disagree with the row.
 */
export function PoiRowActions({ poi, area, dma, isMaximised, onLocate, onShowDetails }: PoiRowActionsProps) {
    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        return () => {
            clearTimeout(copyTimerRef.current);
        };
    }, []);

    const handleCopy = useCallback(() => {
        const properties = mapTablePoiToFeatureProperties(poi, { area, dma });
        // The existing clipboard template, verbatim — no second format.
        const text = buildPoiClipboardText(properties, tablePoiCoords(poi));

        navigator.clipboard.writeText(text).then(
            () => {
                setCopied(true);
                clearTimeout(copyTimerRef.current);
                copyTimerRef.current = setTimeout(() => {
                    setCopied(false);
                }, COPIED_FEEDBACK_MS);
            },
            (error: unknown) => {
                showErrorToast(error);
            },
        );
    }, [area, dma, poi]);

    const handleShowDetails = useCallback(() => {
        onShowDetails(mapTablePoiToFeatureProperties(poi, { area, dma }), tablePoiCoords(poi));
    }, [area, dma, onShowDetails, poi]);

    return (
        // Copy → Show Details → Locate. Locate is last so it holds the same right-most position in
        // both layouts: in split it is the only action, and the two maximised-only actions grow
        // leftwards from it rather than pushing it along the row.
        <div className="flex items-center justify-end gap-1">
            {isMaximised && (
                <>
                    <RowAction
                        label={copied ? 'Copied' : `Copy stop ${String(poi.stop)} details`}
                        tooltip={copied ? 'Copied' : 'Copy To Clipboard'}
                        onClick={handleCopy}
                    >
                        {/* size PROP as well as class, on every icon here: absoluteStrokeWidth
                            divides by the prop, so without it the CSS downscale thins the stroke
                            to 1px instead of the design's true 1.5px. */}
                        {/* The success Check keeps its green under hover — only the blue action
                            icons deepen to Origin Blue Secondary. */}
                        {copied ? (
                            <Check
                                className="text-origin-green size-4"
                                size={16}
                                strokeWidth={1.5}
                                absoluteStrokeWidth
                            />
                        ) : (
                            <Copy
                                className="text-origin-blue group-hover/action:text-origin-blue-secondary size-4"
                                size={16}
                                strokeWidth={1.5}
                                absoluteStrokeWidth
                            />
                        )}
                    </RowAction>
                    <RowAction
                        label={`Show details for stop ${String(poi.stop)}`}
                        tooltip="Show Details"
                        onClick={handleShowDetails}
                    >
                        <PanelRightOpen
                            className="text-origin-blue group-hover/action:text-origin-blue-secondary size-4"
                            size={16}
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                        />
                    </RowAction>
                </>
            )}

            <RowAction
                label={`Locate stop ${String(poi.stop)} on the map`}
                tooltip="Locate on Map"
                onClick={() => {
                    onLocate(poi);
                }}
            >
                <Locate
                    className="text-origin-blue group-hover/action:text-origin-blue-secondary size-4"
                    size={16}
                    strokeWidth={1.5}
                    absoluteStrokeWidth
                />
            </RowAction>
        </div>
    );
}

/**
 * Note the explicit `text-origin-blue` on every icon passed in here.
 *
 * shared-ui colours ghost buttons via `[data-slot="button"][data-variant="ghost"]`, but Base UI's
 * TooltipTrigger overwrites `data-slot` with `tooltip-trigger` on the element it renders. The
 * selector therefore does not match a tooltip-wrapped Button and the icons fall back to the default
 * foreground. Do not drop the class on the assumption that the icon inherits the brand colour.
 */
function RowAction({
    label,
    tooltip,
    onClick,
    children,
}: {
    label: string;
    tooltip: string;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    // hover:bg-accent replaces the ghost variant's muted grey (the shared
                    // accent-hover rule never lands here — TooltipTrigger overwrites the Button's
                    // data-slot; see the icon-colour note above). The dark twin is needed too: the
                    // variant's dark:hover:bg-muted/50 sits on a different variant stack, so
                    // tailwind-merge keeps it unless restated. `group/action` lets each passed-in
                    // icon deepen to Origin Blue Secondary on hover.
                    <Button
                        variant="ghost"
                        size="icon"
                        className="group/action hover:bg-accent dark:hover:bg-accent size-7"
                        aria-label={label}
                        onClick={onClick}
                    >
                        {children}
                    </Button>
                }
            />
            <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
    );
}
